import React, { FC } from "react";
import { FormLabel } from "@UI/Components/Form/Form";
import { PhotoAttachmentId } from "@Shared/services/photoAttachments";
import { IPhotoAttachment } from "../../../types";
import { HintsBox } from "../HintsBox/HintsBox";
import { QuestionFormControl } from "../QuestionFormControl/QuestionFormControl";
import { BaseQuestionProps } from "../types";
import { PhotoUploader, PhotoUploaderProps } from "./PhotoUploader";

export interface PictureUploadQuestionProps extends BaseQuestionProps {
  /** Omitted when the screen's `<h1>` is already this question's title. */
  label?: string;
  /** Resolved `hints_intro` — the bold line above the hint list. */
  hintsTitle?: string;
  /** Resolved hint copy, in config order (see `utils/resolveHints.ts`). */
  hints?: string[];
  /** Attachment ids, the form value — matches what `buildScreenSchema.ts`
   * validates for `picture_upload`, and what the backend takes. */
  value: number[];
  onChange: (value: number[]) => void;
  /** Called once a caption has reached its attachment — see `PhotoUploader`. */
  onDescriptionSaved?: (id: number, description: string) => void;
  /** `draft.photo_attachments` — thumbnails and captions for photos already on
   * the draft. */
  existingPhotos?: IPhotoAttachment[];
  /** Merges a freshly uploaded/removed photo into the cached draft, so its
   * thumbnail survives navigating away from this screen and back (see
   * `useRecordUploadedPhoto`). */
  onPhotoUploaded?: (photo: IPhotoAttachment) => void;
  onPhotoRemoved?: (id: number) => void;
  /** The draft's own id, for linking these photos to it later — not needed
   * to upload them (see `PhotoUploader`). */
  photoAttachableId?: PhotoAttachmentId;
  category: string;
  maxFiles: number;
  allowedExtensions: string[];
  captionMaxLength: number;
  /** Reported while an upload or a removal is in flight, so the screen can
   * hold Continue until it's done. */
  onBusyChange?: (isBusy: boolean) => void;
  columns?: PhotoUploaderProps["columns"];
}

/**
 * The photos screen's content: the question's label, the hint box, and the
 * uploader.
 *
 * The hint box sits above the uploader, matching both the design and
 * `TextQuestion` — the hints tell the user what to photograph, so they have to
 * be read before the picker opens, not after.
 *
 * Presentational, like every other question component (PLAN.md decision 11):
 * the config, the conditional hint resolution and the constraint mapping all
 * happen in `config/questionRegistry.tsx`.
 */
export const PictureUploadQuestion: FC<PictureUploadQuestionProps> = ({
  label,
  hintsTitle,
  hints,
  value,
  onChange,
  onDescriptionSaved,
  existingPhotos,
  onPhotoUploaded,
  onPhotoRemoved,
  photoAttachableId,
  category,
  maxFiles,
  allowedExtensions,
  captionMaxLength,
  onBusyChange,
  columns,
  isRequired = false,
  isInvalid = false,
  errorMessage = "",
  isDisabled = false,
}) => (
  <QuestionFormControl
    isRequired={isRequired}
    isInvalid={isInvalid}
    errorMessage={errorMessage}
    isDisabled={isDisabled}
  >
    {label && <FormLabel>{label}</FormLabel>}
    <HintsBox title={hintsTitle} hints={hints} />
    <PhotoUploader
      value={value}
      onChange={onChange}
      onDescriptionSaved={onDescriptionSaved}
      existingPhotos={existingPhotos}
      onPhotoUploaded={onPhotoUploaded}
      onPhotoRemoved={onPhotoRemoved}
      photoAttachableId={photoAttachableId}
      category={category}
      maxFiles={maxFiles}
      allowedExtensions={allowedExtensions}
      captionMaxLength={captionMaxLength}
      isDisabled={isDisabled}
      onBusyChange={onBusyChange}
      columns={columns}
    />
  </QuestionFormControl>
);
